import { Suspense, useContext } from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { title } from "process";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";
import { twJoin } from "tailwind-merge";

import {
  TitleBar,
  ErrorBox,
  LinkButton,
  Price,
  PrimaryAction,
} from "@onsite_app/MobileOrderingMenu/Atoms";
import NavBarTitle, {
  NavBarContext,
} from "@onsite_app/MobileOrderingMenu/Atoms/Navbar";
import { useBrand } from "@onsite_app/MobileOrderingMenu/BrandContext";
import Loading from "@onsite_app/MobileOrderingMenu/Loading";
import { LogoImage } from "@onsite_app/MobileOrderingMenu/MainPage";
import { useVenue } from "@onsite_app/MobileOrderingMenu/VenueContext";
import { usePageData } from "@onsite_app/MobileOrderingMenu/queries";
import { Venue } from "@onsite_app/MobileOrderingMenu/schema";

import { SocialLinks } from "../Checkout/Layout";
import { useTimeoutSearchParam } from "../Checkout/useTimeoutSearchParam";
import VenueOnlineConfigQueries from "../VenueOnlineConfig/queries";
import VoucherCartItemsQueries from "./VoucherCartItems/queries";
import { VoucherTemplateCard } from "./VoucherTemplates/VoucherTemplateCard";
import VoucherTemplatesQueries from "./VoucherTemplates/queries";
import { VouchersNotEnabled } from "./VouchersLayout";
import { VoucherCartQueries } from "./queries";

const hasLogoImage = ({ logoImageUrl }: Venue) =>
  logoImageUrl && logoImageUrl != "/assets/missing.png";

const LandingPage = () => {
  const { t } = useTranslation();
  const vouchersEnabled = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
    select: (data) => data.onlineVoucherSales,
  }).data;
  if (!vouchersEnabled) return <VouchersNotEnabled />;

  const menuUuid = usePageData().menuUUID;
  const venue = useVenue();
  const hasJustCancelledPayment = useTimeoutSearchParam("payment");
  const navBarContent = useContext(NavBarContext);
  const venueTempatesQuery = useSuspenseQuery(VoucherTemplatesQueries.all());

  const voucherCartQuery = useSuspenseQuery(VoucherCartQueries.current);
  const voucherCart = voucherCartQuery.data;
  const voucherCartItems = useSuspenseQuery(
    VoucherCartItemsQueries.cartItems(voucherCart.uuid),
  ).data;

  const voucherConfig = useSuspenseQuery(
    VenueOnlineConfigQueries.current(),
  ).data;
  const { isBrutalist } = useBrand();
  return (
    <Suspense fallback={<Loading loadingState="LOADING" />}>
      <NavBarTitle title={t("vouchers.title")} />
      <div className="mx-auto max-w-screen-sm space-y-12 py-16">
        {hasLogoImage(venue) && (
          <div className="mx-auto w-96">
            <Link
              to={`/`}
              className="mx-auto block w-full max-w-[96px] space-y-8 text-center"
            >
              <LogoImage />
            </Link>
          </div>
        )}
        <div className="space-y-16">
          <div className="text-center text-xl font-extrabold text-venue-primary">
            {venue.name}
          </div>
          <div className="text-sm mx-auto whitespace-pre-wrap font-normal">
            {venue.venueBrand.description}
          </div>
          <div className="flex justify-center">
            <SocialLinks />
          </div>
        </div>
      </div>
      <div className="space-y-32 pb-64 md:pb-24">
        <div className="space-y-32">
          {hasJustCancelledPayment && (
            <div className="mx-auto max-w-screen-sm">
              <ErrorBox>{t("cart.paymentCancelled")}</ErrorBox>
            </div>
          )}
          <div className="space-y-12">
            <TitleBar hideBackButton={!menuUuid}>
              <div className="md:text-4xl md:font-bold">
                {navBarContent.navTitle || title}
              </div>
            </TitleBar>
            <div className="rounded-venue-brand border-venue-tertiary md:mx-auto md:max-w-screen-sm md:space-y-32 md:p-16">
              <div>
                <NavBarTitle title="Vouchers" />
                <div
                  className={twJoin(
                    "mx-auto max-w-screen-sm",
                    isBrutalist ? "space-y-24" : "space-y-16",
                  )}
                >
                  {venueTempatesQuery.data.map((template) => (
                    <div key={template.uuid} className="">
                      <Link
                        to={`items/new?voucherTemplateUuid=${template.uuid}`}
                      >
                        <VoucherTemplateCard voucherTemplate={template} />
                      </Link>
                    </div>
                  ))}

                  {voucherConfig.customVoucherAmounts && (
                    <div className="text-center">
                      <LinkButton secondary to="items/new">
                        {t("vouchers.buyCustomAmountVoucher")}
                      </LinkButton>
                    </div>
                  )}

                  {voucherCartQuery.data && (
                    <div>
                      <div>
                        <div className="hidden items-center justify-between space-x-32 md:flex">
                          <div className="flex-none text-2xl font-extrabold">
                            <Price
                              showCurrency
                              fromCents
                              value={voucherCartItems.reduce(
                                (total, item) => total + item.valueInCents,
                                0,
                              )}
                            />
                          </div>
                          <div className="flex-1">
                            <LinkButton to="checkout" primary>
                              {t("vouchers.yourVouchers")}
                            </LinkButton>
                          </div>
                        </div>
                      </div>
                      <PrimaryAction>
                        <LinkButton to="checkout" primary>
                          {t("vouchers.yourVouchers")}{" "}
                          <Price
                            showDecimals
                            showCurrency
                            fromCents
                            value={voucherCartItems.reduce(
                              (total, item) => total + item.valueInCents,
                              0,
                            )}
                          />
                        </LinkButton>
                      </PrimaryAction>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default LandingPage;
